import { useState, useEffect, useCallback, useRef } from "react";
import { GroupCreator } from "@/components/groups/GroupCreator";
import { Button } from "@/components/ui/button";
import { Loader2, Plus, RefreshCw } from "lucide-react";
import { Header } from "@/components/layout/Header";
import { GroupList } from "@/components/groups/GroupList";
import { useGroups } from "@/hooks/useGroups";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Skeleton } from "@/components/ui/skeleton";
import { safeSelect } from "@/utils/supabaseHelpers";
const Index = () => {
  const {
    t
  } = useTranslation();
  const [userName, setUserName] = useState("");
  const [userLoading, setUserLoading] = useState(true);
  const [showGroupCreator, setShowGroupCreator] = useState(false);
  const {
    groups,
    loading,
    error,
    initialized,
    handleGroupClick,
    refreshGroups
  } = useGroups();
  const navigate = useNavigate();
  const isMounted = useRef(true);
  const lastRefreshRef = useRef(Date.now());
  const refreshTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const touchStartY = useRef(0);
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        setUserLoading(true);
        const {
          data: {
            session
          }
        } = await supabase.auth.getSession();
        if (!session?.user?.id || !isMounted.current) return;

        // Use cache for user data to improve performance
        const cachedUser = localStorage.getItem('user-profile');
        const cacheTime = localStorage.getItem('user-profile-time');
        const isCacheValid = cacheTime && Date.now() - parseInt(cacheTime) < 5 * 60 * 1000; // 5 minutes

        if (cachedUser && isCacheValid) {
          const userData = JSON.parse(cachedUser);
          // Prioritize name over username or email
          if (userData.name) {
            setUserName(userData.name);
          } else if (userData.username) {
            setUserName(userData.username);
          } else if (session.user.email) {
            setUserName(session.user.email.split('@')[0]);
          } else {
            setUserName(t('common.user'));
          }
          setUserLoading(false);
          return;
        }

        // Use safer select method
        const response = await safeSelect<{
          name: string;
          username: string;
        }>('profiles', {
          id: session.user.id
        }, {
          single: true
        });
        if (isMounted.current) {
          if (response.success && response.data) {
            // Prioritize name over username or email
            if (response.data.name) {
              setUserName(response.data.name);
            } else if (response.data.username) {
              setUserName(response.data.username);
            } else if (session.user.email) {
              setUserName(session.user.email.split('@')[0]);
            } else {
              setUserName(t('common.user'));
            }

            // If name or username is missing, update them using email
            let updatedFields = {};
            let needsUpdate = false;
            if (!response.data.name) {
              updatedFields = {
                ...updatedFields,
                name: session.user.email?.split('@')[0] || t('common.user')
              };
              needsUpdate = true;
            }
            if (!response.data.username) {
              updatedFields = {
                ...updatedFields,
                username: session.user.email?.split('@')[0] || t('common.user')
              };
              needsUpdate = true;
            }
            if (needsUpdate) {
              await supabase.from('profiles').update(updatedFields).eq('id', session.user.id);

              // Update the cached data with the new fields
              const updatedData = {
                ...response.data,
                ...updatedFields
              };
              localStorage.setItem('user-profile', JSON.stringify(updatedData));
              localStorage.setItem('user-profile-time', Date.now().toString());
            } else {
              // Cache user data as is
              localStorage.setItem('user-profile', JSON.stringify(response.data));
              localStorage.setItem('user-profile-time', Date.now().toString());
            }
          } else {
            // Fallback to email if profile not found
            setUserName(session.user.email?.split('@')[0] || t('common.user'));

            // Try to create/update profile with basic info
            await supabase.from('profiles').upsert({
              id: session.user.id,
              name: session.user.email?.split('@')[0] || t('common.user'),
              username: session.user.email?.split('@')[0] || t('common.user'),
              email: session.user.email
            });
          }
        }
      } catch (e) {
        console.error("Error fetching user data:", e);
        // Use fallback name if there's an error
        const {
          data: {
            session
          }
        } = await supabase.auth.getSession();
        if (session?.user) {
          setUserName(session.user.email?.split('@')[0] || t('common.user'));
        }
      } finally {
        setUserLoading(false);
      }
    };
    fetchUserData();

    // Auth subscription
    const {
      data: {
        subscription: authSubscription
      }
    } = supabase.auth.onAuthStateChange((_event, session) => {
      if (!session && isMounted.current) {
        navigate('/login');
      }
    });
    return () => {
      isMounted.current = false;
      authSubscription.unsubscribe();
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
      }
    };
  }, [navigate, t]);
  const handleGroupCreationSuccess = useCallback(() => {
    setShowGroupCreator(false);
    refreshGroups();
  }, [refreshGroups]);

  // Add pull-to-refresh functionality for mobile
  const setupPullToRefresh = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;
    const onTouchStart = (e: TouchEvent) => {
      const scrollTop = container.scrollTop;
      // Only enable pull-to-refresh when at the top of the page
      if (scrollTop <= 0) {
        touchStartY.current = e.touches[0].clientY;
      }
    };
    const onTouchMove = (e: TouchEvent) => {
      // Don't do anything if we're not at the top
      if (container.scrollTop > 0) return;
      const touchY = e.touches[0].clientY;
      const touchDiff = touchY - touchStartY.current;

      // Only activate if pulling down more than 60px
      if (touchDiff > 60 && !isRefreshing) {
        setIsRefreshing(true);

        // Throttle refreshes to prevent multiple calls
        const now = Date.now();
        if (now - lastRefreshRef.current > 5000) {
          // 5 seconds minimum between refreshes
          lastRefreshRef.current = now;
          refreshGroups();

          // Auto-hide refresh indicator after 1 second
          refreshTimeoutRef.current = setTimeout(() => {
            setIsRefreshing(false);
          }, 1000);
        } else {
          // Just hide the indicator if we can't refresh yet
          refreshTimeoutRef.current = setTimeout(() => {
            setIsRefreshing(false);
          }, 500);
        }
      }
    };
    const onTouchEnd = () => {
      // Reset the state when touch ends
      touchStartY.current = 0;
      if (isRefreshing) {
        refreshTimeoutRef.current = setTimeout(() => {
          setIsRefreshing(false);
        }, 500);
      }
    };
    container.addEventListener('touchstart', onTouchStart);
    container.addEventListener('touchmove', onTouchMove);
    container.addEventListener('touchend', onTouchEnd);
    return () => {
      container.removeEventListener('touchstart', onTouchStart);
      container.removeEventListener('touchmove', onTouchMove);
      container.removeEventListener('touchend', onTouchEnd);
    };
  }, [isRefreshing, refreshGroups]);
  useEffect(() => {
    return setupPullToRefresh();
  }, [setupPullToRefresh]);

  // Make sure to always return a component even while loading
  return <div className="min-h-screen w-full bg-gradient-to-b from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800">
      <Header />
      <div ref={containerRef} className="container mx-auto p-4 space-y-8 pb-24 overflow-auto h-[calc(100vh-64px)]">
        {/* Pull-to-refresh indicator */}
        {isRefreshing && <div className="flex justify-center items-center py-3 transition-all">
            <RefreshCw className="animate-spin text-primary h-5 w-5 mr-2" />
            <span className="text-sm text-primary">{t('common.refreshing', 'Refreshing...')}</span>
          </div>}
        
        {/* Welcome Section */}
        <div className="text-center space-y-2">
          {userLoading ? <>
              <Skeleton className="h-10 w-48 mx-auto mb-2 bg-gradient-to-r from-primary/10 to-green/10" />
              <Skeleton className="h-6 w-64 mx-auto bg-gradient-to-r from-primary/10 to-green/10" />
            </> : <>
              <h1 className="font-bold text-xl text-gray-950">
                {t('common.greeting', {
              name: userName
            })} 👋
              </h1>
              <p className="text-muted-foreground text-base">
                {!initialized ? t('groups.loading', 'Loading your groups...') : error ? t('groups.loadingError', 'There was a problem loading your groups') : groups.length > 0 ? t('groups.activeGroupsCount', {
              count: groups.length
            }) : t('groups.noActiveGroups', 'You have no active groups')}
              </p>
            </>}
        </div>

        {/* Create Group Button - Always visible unless creating */}
        {!showGroupCreator && <div className="flex justify-center">
            <Button onClick={() => setShowGroupCreator(true)} className="bg-gradient-to-r from-primary to-green hover:from-primary/90 hover:to-green/90 transition-all duration-300 text-lg py-6 px-8 rounded-xl shadow-md">
              <Plus className="mr-2 h-5 w-5" />
              {t('groups.createNew', 'Create New Group')}
            </Button>
          </div>}

        {/* Group Creator Modal */}
        {showGroupCreator && <GroupCreator onSuccess={handleGroupCreationSuccess} />}

        {/* Groups List with optimized loading handling */}
        <GroupList groups={groups} onGroupClick={handleGroupClick} loading={loading && !initialized} onRefresh={refreshGroups} error={error} />
      </div>
    </div>;
};
export default Index;